<template>
  <div
    id="myCoach"
    :class="`${$mq}Mode`"
  >
    <MyCoachInfo />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import api from '@/api';
import MyCoachInfo from './MyCoachInfo.vue';

export default {
  components: {
    MyCoachInfo,
  },

  props: {
    policyAgreed: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      pastMeetings: null,
      nextMeetings: null,
      sessionNextMeeting: null,
      loading: false,
      propLoading: false,
    };
  },

  computed: {
    ...mapState({
      coach: (state) => state.myCoach.coach,
      status: (state) => state.processStatus.process,
      userTimezone: (state) => state.user.user.time_zone,
    }),

    ...mapGetters('processStatus', {
      nextSession: 'nextSession',
      currentSession: 'currentSession',
    }),

    orderedPastSession() {
      if (this.pastMeetings && Object.keys(this.pastMeetings).length > 0) {
        return Object.keys(this.pastMeetings).reverse();
      }
      return [];
    },

    responsiveCol() {
      const col = {};
      if (this.$mq === 'mobile') {
        col.one = 24;
        col.two = 24;
      } else {
        col.one = 8;
        col.two = 16;
      }
      return col;
    },
  },

  watch: {
    currentSession() {
      this.getUpcomingMeeting();
    },
  },

  created() {
    this.getUpcomingMeeting();
  },

  methods: {
    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    schedulerIcon(status) {
      let icon;
      switch (status) {
        case 'cancelled':
          icon = 'el-icon-close';
          break;
        case 'confirmed':
        case 'rescheduled':
          icon = 'el-icon-refresh';
          break;
        default:
          icon = 'el-icon-date';
      }

      return icon;
    },

    async getUpcomingMeeting() {
      if (!this.currentSession) return;
      try {
        this.loading = true;
        const response = await api.getUpcomingMeetings();
        if (Object.entries(response.data).length === 0) {
          this.pastMeetings = null;
          this.nextMeetings = [{}];
          this.sessionNextMeeting = this.currentSession === 'on_boarding'
            ? this.nextSession
            : this.currentSession;
        } else {
          const meetings = response.data;
          this.sessionNextMeeting = this.currentSession;

          if (meetings[this.currentSession]
            && meetings[this.currentSession].some((el) => el.status === 'completed')) {
            this.sessionNextMeeting = this.nextSession;
          }
          if (meetings[this.sessionNextMeeting]) {
            this.nextMeetings = meetings[this.sessionNextMeeting].reverse();
            if (this.nextMeetings.every((el) => el.status === 'cancelled')) {
              this.nextMeetings.unshift({});
            }
          } else {
            this.nextMeetings = [{}];
          }
          delete meetings[this.sessionNextMeeting];

          // Past meeting will be ordered by status
          Object.keys(meetings).forEach((session) => {
            const order = {
              completed: 1,
              rescheduled: 2,
              cancelled: 3,
            };
            meetings[session].sort((a, b) => order[a.status] - order[b.status]);
          });

          this.pastMeetings = meetings;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },

    addMeeting(oldMeeting, newMeeting) {
      const meeting = this.nextMeetings.find((el) => el.uuid === oldMeeting.uuid);
      if (meeting && meeting.status) {
        meeting.status = 'cancelled';
      } else {
        this.nextMeetings.shift();
      }

      this.nextMeetings.unshift(newMeeting);
      this.fetchProcess();
    },

    async clickCancelMeeting(meeting) {
      const isLessThan48Hours = this.$moment()
        .tz(this.userTimezone)
        .diff(meeting.start_time, 'hours') > -48;

      if (isLessThan48Hours) {
        this.$prompt(this.$t('leader.coach.confirmLess48'),
          {
            inputType: 'textarea',
            showClose: false,
            showCancelButton: true,
            confirmButtonText: this.$t('leader.coach.cancelMeeting'),
            cancelButtonText: this.$t('common.goBack'),
            roundButton: true,
            cancelButtonClass: 'el-button--info promptBtn',
            confirmButtonClass: 'promptBtn',
            customClass: 'coachMsgBox',
            inputValidator: (value) => {
              if (!value) {
                return this.$t('leader.coach.pleaseInputReason');
              }
              return true;
            },
            inputErrorMessage: 'Invalid reason',
          }).then(async ({ value }) => {
          console.log(value);
          this.cancelMeeting(meeting, value);
        }).catch(() => {});
      } else {
        this.$msgbox({
          message: this.$t('leader.coach.confirmCancel', {
            coachName: `${this.coach.first_name} ${this.coach.last_name}`,
            date: `${meeting.start_time}`,
          }),
          showClose: false,
          showCancelButton: true,
          confirmButtonText: this.$t('common.cancel'),
          cancelButtonText: this.$t('common.goBack'),
          roundButton: true,
          cancelButtonClass: 'el-button--info',
          customClass: 'coachMsgBox',
        }).then(async () => {
          this.cancelMeeting(meeting);
        }).catch(() => {});
      }
    },

    async cancelMeeting(meeting, reason) {
      try {
        this.propLoading = true;
        let response;
        if (!reason) {
          response = await api.cancelCoachMeeting(meeting.uuid);
        } else {
          response = await api.cancelCoachMeeting(meeting.uuid, { reason });
        }

        // update meeting status in upcoming meetings list
        const index = this.nextMeetings.findIndex((el) => el.uuid === meeting.uuid);
        this.nextMeetings[index].status = response.data.status;
        this.nextMeetings.unshift({});
        this.fetchProcess();
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.propLoading = false;
      }
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="scss">
#myCoach {
  .coachCard {
    margin-bottom: 15px;

    &.loadingCard {
      min-height: 105px;
    }

    .title {
      font-weight: bold;
    }

    .layout {
      display: flex;
      text-align: left;

      & > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .col1 {
        width: 15%;
        font-weight: bold;
        line-height: 30px;
      }

      .col2 {
        width: 20%;
        text-align: center;

        & > .el-image {
          margin: 0 auto;
        }
      }

      .col3 {
        width: 35%;
      }

      .col4 {
        padding-left: 5%;
      }

      .name {
        font-weight: bold;
      }

      .email {
        font-style: italic;
      }

      .profileLink {
        width: 100%;
        min-width: 150px;
      }

      &.scheduleNextMeeting {
        margin-top: 10px;
      }
    }

    .layoutMobile {
      width: 100%;
    }
  }

  &.mobileMode {
    .layout {
      display: block;
      width: 100%;
      margin-left: 0 !important;

      &.scheduleNextMeeting {
        .sessionName {
          display: block;
          width: 100%;
          margin-bottom: 13px;
        }

        .meetingTime {
          display: block;
          width: 100%;
        }
      }
    }
  }
}

.coachMsgBox {
  .promptBtn {
    padding: 11px 15px !important;
    width: unset !important; // width should not be set in app.vue
    max-width: unset !important;
  }
}
</style>
